import React, { useCallback, useEffect, useRef } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import useHoverNavigation from '../use-hover-navigation';
import { hoverHighlightSx } from '../hover-style';

const panelSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'box-shadow 0.3s ease, filter 0.3s ease',
    borderRadius: 4,
};

export default function ChatScreen({ messages, options, loading, onSelectOption, onBack }) {
    const { t } = useTranslation();
    const chatEndRef = useRef(null);

    const itemCount = (options?.length || 0) + 1; // options + Back

    const onSelect = useCallback((index) => {
        if (loading) return;
        if (index < (options?.length || 0)) {
            onSelectOption(options[index]);
        } else {
            onBack();
        }
    }, [options, loading, onSelectOption, onBack]);

    const { active, activeIndex } = useHoverNavigation(itemCount, onSelect);

    // Auto-scroll chat to bottom
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    return (
        <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
            {/* Left panel — Chat history */}
            <Box sx={{ flex: '0 0 60%', display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider' }}>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {messages.map((msg, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: 'flex',
                                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                            }}
                        >
                            <Paper
                                elevation={1}
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    maxWidth: '80%',
                                    bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                                    color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                                    borderRadius: msg.role === 'user'
                                        ? '16px 16px 4px 16px'
                                        : '16px 16px 16px 4px',
                                }}
                            >
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {msg.content}
                                </Typography>
                            </Paper>
                        </Box>
                    ))}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Paper
                                elevation={1}
                                sx={{ px: 3, py: 2, bgcolor: 'grey.100', borderRadius: '16px 16px 16px 4px' }}
                            >
                                <CircularProgress size={24} />
                            </Paper>
                        </Box>
                    )}
                    <div ref={chatEndRef} />
                </Box>
            </Box>

            {/* Right panel — Options */}
            <Box sx={{ flex: '0 0 40%', display: 'flex', flexDirection: 'column', p: 2, gap: 1.5 }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {(options || []).map((option, index) => {
                        const isHovered = active && activeIndex === index;
                        return (
                            <Box
                                key={index}
                                onClick={() => !loading && onSelectOption(option)}
                                sx={{
                                    ...panelSx,
                                    flex: 1,
                                    border: '3px solid',
                                    borderColor: loading ? 'grey.300' : 'primary.main',
                                    color: loading ? 'text.disabled' : 'primary.main',
                                    px: 2,
                                    opacity: loading ? 0.5 : 1,
                                    pointerEvents: loading ? 'none' : 'auto',
                                    ...(isHovered && !loading && hoverHighlightSx),
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
                                    {option}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                <Box
                    onClick={onBack}
                    sx={{
                        ...panelSx,
                        flexDirection: 'row',
                        gap: 1,
                        bgcolor: 'grey.300',
                        color: 'text.primary',
                        py: 2,
                        ...(active && activeIndex === (options?.length || 0) && hoverHighlightSx),
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('APPLICATIONS.BACK')}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
